import { Home, MessageCircle } from "lucide-react";
import { IoIosArrowBack } from "react-icons/io";
import { useState, useEffect } from "react";
import clsx from "clsx";

/**
 * Banner offset constants for OneTapSignInBanner positioning
 * These values must match the actual rendered heights of the banner component
 * to prevent overlap with the Toggle component.
 */
const BANNER_OFFSETS = {
    STEP_1: "70px",
    STEP_2: "200px",
} as const;

interface ToggleTabMenuProps {
    activeTab: "guesses" | "scoreboard";
    onTabChange: (tab: "guesses" | "scoreboard") => void;
    scoreboardCount?: number;
    showBanner?: boolean;
    isMobile?: boolean;
    bannerStep?: 1 | 2;
    /** `inset` on listing hero; `fixed` viewport-centered overlay; `scoreboard-bar` in the 600px scoreboard column; `sticky-pill` pins the floating pill just below the header on scroll. */
    placement?: "fixed" | "inset" | "scoreboard-bar" | "sticky-pill";
    /** Shown on the scoreboard tab bar (left of Listing / Scoreboard), e.g. return to feed. */
    onBackClick?: () => void;
}

function getDeviceType() {
    const width = window.innerWidth;
    const userAgent = navigator.userAgent;
    const platform = (navigator as any).platform || "";
    const maxTouchPoints = (navigator as any).maxTouchPoints || 0;
    const isIPad = /iPad/.test(userAgent) || (platform === "MacIntel" && maxTouchPoints > 1);
    if (isIPad) {
        return "ipad";
    }
    if (width < 768) return "mobile";
    if (width < 1024) return "tablet";
    return "desktop";
}

export default function Toggle({
    activeTab,
    onTabChange,
    scoreboardCount,
    showBanner = false,
    isMobile: isMobileProp,
    bannerStep = 1,
    placement = "fixed",
    onBackClick,
}: ToggleTabMenuProps) {
    const [deviceType, setDeviceType] = useState(getDeviceType());
    const isMobileDevice = isMobileProp ?? deviceType === "mobile";
    const effectiveDeviceType = isMobileProp ? "mobile" : deviceType;

    useEffect(() => {
        const handleResize = () => setDeviceType(getDeviceType());
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const getTopPosition = () => {
        let basePosition: string;
        switch (effectiveDeviceType) {
            case "mobile":
                basePosition = "calc(env(safe-area-inset-top, 0px) + clamp(80px, 10svh, 100px))";
                break;
            case "ipad":
                basePosition = "calc(env(safe-area-inset-top, 0px) + clamp(100px, 12svh, 130px))";
                break;
            case "tablet":
                basePosition = "calc(env(safe-area-inset-top, 0px) + clamp(120px, 15svh, 140px))";
                break;
            case "desktop":
                basePosition = "calc(env(safe-area-inset-top, 0px) + clamp(75px, 10svh, 100px))";
                break;
            default:
                basePosition = "calc(env(safe-area-inset-top, 0px) + clamp(80px, 10svh, 100px))";
        }
        if (isMobileDevice && showBanner) {
            const bannerOffset = bannerStep === 2 ? BANNER_OFFSETS.STEP_2 : BANNER_OFFSETS.STEP_1;
            return `calc(${basePosition} + ${bannerOffset})`;
        }
        return basePosition;
    };

    const isInsetOnHero = placement === "inset";
    const isScoreboardBar = placement === "scoreboard-bar";
    const isStickyPill = placement === "sticky-pill";
    const showBackOnTabBar = Boolean(onBackClick) && isScoreboardBar;
    const tabPill = (
        <div
            className="tw-rounded-full tw-max-w-max tw-pointer-events-auto"
            style={{
                background: "rgba(20, 14, 40, 0.72)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                borderRadius: 22,
                padding: 1,
                gap: 2,
                boxShadow: "0 2px 12px rgba(0,0,0,0.4), inset 0 0.5px 0 rgba(255,255,255,0.15)",
                border: "1px solid rgba(0,0,0,0.2)",
            }}
        >
            <div className="tw-flex">
                <button
                    type="button"
                    onClick={() => onTabChange("guesses")}
                    className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium"
                    style={{
                        height: 30,
                        padding: "0 16px",
                        borderRadius: 18,
                        border: "none",
                        background: activeTab === "guesses" ? "rgba(255,255,255,0.2)" : "transparent",
                        transition: "background 0.2s",
                        color: activeTab === "guesses" ? "white" : "rgba(255,255,255,0.5)",
                    }}
                >
                    <Home size={13} color={activeTab === "guesses" ? "white" : "rgba(255,255,255,0.5)"} />
                    Listing
                </button>
                <button
                    type="button"
                    onClick={() => onTabChange("scoreboard")}
                    className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium"
                    style={{
                        height: 30,
                        padding: "0 16px",
                        borderRadius: 18,
                        border: "none",
                        background: activeTab === "scoreboard" ? "rgba(255,255,255,0.2)" : "transparent",
                        transition: "background 0.2s",
                        color: activeTab === "scoreboard" ? "white" : "rgba(255,255,255,0.5)",
                    }}
                >
                    <MessageCircle
                        size={13}
                        color={activeTab === "scoreboard" ? "white" : "rgba(255,255,255,0.5)"}
                    />
                    Scoreboard
                    {scoreboardCount !== undefined && scoreboardCount > 0 && (
                        <span className="tw-text-white tw-ml-1">{`(${scoreboardCount})`}</span>
                    )}
                </button>
            </div>
        </div>
    );
    const tabBarRow = showBackOnTabBar ? (
        <div className="tw-flex tw-items-center tw-w-full tw-gap-2">
            <button
                type="button"
                className="tw-flex-shrink-0 tw-h-10 tw-w-10 tw-bg-black/70 tw-backdrop-blur-md tw-text-white tw-rounded-full tw-shadow-[0_2px_10px_rgba(0,0,0,0.45)] tw-ring-1 tw-ring-black/25 tw-transition-all tw-flex tw-items-center tw-justify-center tw-p-2 tw-pointer-events-auto"
                onClick={onBackClick}
                aria-label="Go back"
            >
                <IoIosArrowBack className="tw-h-6 tw-w-6 tw-text-white tw-cursor-pointer" />
            </button>
            <div className="tw-flex-1 tw-flex tw-justify-center tw-min-w-0 tw-pointer-events-auto">{tabPill}</div>
            <div className="tw-flex-shrink-0 tw-h-10 tw-w-10" aria-hidden="true" />
        </div>
    ) : (
        <div className="tw-flex tw-justify-center tw-w-full tw-pointer-events-auto">{tabPill}</div>
    );
    if (isStickyPill) {
        // Zero-height sticky wrapper: overlaps the top of the photo at scroll-top (no push-down),
        // then pins the translucent pill just below the fixed header as the user scrolls.
        // `top` follows --priceme-header-height so it tracks the mobile header collapse.
        return (
            <div
                className="tw-sticky tw-z-[50] tw-h-0 tw-w-full tw-flex tw-justify-center tw-pointer-events-none"
                style={{ top: "var(--priceme-header-height, 9svh)" }}
            >
                <div className="tw-pt-3 tw-pointer-events-auto">{tabPill}</div>
            </div>
        );
    }
    if (isScoreboardBar) {
        // Pin just below the fixed header (not at viewport top-0, which would sit behind the header).
        // `top` follows --priceme-header-height so it tracks the mobile header collapse.
        return (
            <div
                className="tw-sticky tw-z-[50] tw-w-full tw-py-3 tw-bg-[#1a1a2e] tw-pointer-events-none"
                style={{ top: "var(--priceme-header-height, 9svh)" }}
            >
                <div className="tw-px-3">{tabBarRow}</div>
            </div>
        );
    }
    return (
        <div
            className={clsx(
                "tw-z-[50] tw-pointer-events-none",
                isInsetOnHero ? "tw-absolute tw-inset-x-0 tw-top-3 tw-flex tw-justify-center" : "tw-fixed tw-w-full tw-left-0",
            )}
            style={isInsetOnHero ? undefined : { top: getTopPosition() }}
        >
            <div className={clsx(!isInsetOnHero && "tw-flex tw-justify-center tw-w-full tw-pointer-events-auto")}>
                {tabBarRow}
            </div>
        </div>
    );
}
